import { object, secondsPer } from '@art-suite/art-core-ts';
import { useMemo } from 'react';

import {
  ApiCreateScanTemplate,
  ApiScanTemplate,
  ApiUpdateScanTemplate,
  getScanTemplateQueryKey,
  getScanTemplateRootQueryKey,
  getScanTemplatesQueryKey,
  KaiProApiClient,
} from '@kaiizen/kai-pro-api';
import { getNormalizedScanTemplateRoot, getScanDataInMemoryDeltaFromScanTemplateRoots, ScanTemplateRoot } from '@kaiizen/kai-scan-template-system';
import { QueryCache, useCachedQuery, useQueryCache } from '@kaiizen/react-persistent-cache';

import { useCombinedCachedQueryStatus } from '@kaiizen/react-persistent-cache';
import { useKaiProApiClient } from '../useKaiProApiClient';
import { useScanTemplateDataMutators } from './ScanTemplateDataMutators';

const fetchAndPopulateSingletonQueries = async (templatesPromise: Promise<ApiScanTemplate[]>, queryCache: QueryCache) => {
  const templateQueryCache = queryCache as unknown as QueryCache<ApiScanTemplate>;
  const templates = await templatesPromise;
  templates.forEach(template => {
    templateQueryCache.cache.set(getScanTemplateQueryKey(template.id), template);
  });
  return templates;
};

const useScanTemplateMutators = (
  kaiProApiClient: KaiProApiClient,
  queryCache: QueryCache<unknown>,
) => {
  const templatesQueryCache = queryCache as QueryCache<ApiScanTemplate[]>;
  const activeQueryKey = getScanTemplatesQueryKey();

  return useMemo(() => ({
    create: async (record: ApiCreateScanTemplate, scanTemplateRoot?: ScanTemplateRoot) => {
      const newRecord: ApiScanTemplate = await kaiProApiClient.createScanTemplate(record, scanTemplateRoot);
      queryCache.cache.set(getScanTemplateQueryKey(newRecord.id), newRecord);
      const currentEntries: ApiScanTemplate[] | undefined = (await templatesQueryCache.cache.getEntry(activeQueryKey))?.value;
      templatesQueryCache.cache.set(activeQueryKey, [newRecord, ...(currentEntries ?? []).filter(s => s.id !== newRecord.id)]);
      return newRecord;
    },
    update: async (id: string, updates: ApiUpdateScanTemplate) => {
      const updatedRecord = await kaiProApiClient.updateScanTemplate(id, updates);
      queryCache.cache.set(getScanTemplateQueryKey(updatedRecord.id), updatedRecord);
      const currentEntries: ApiScanTemplate[] | undefined = (await templatesQueryCache.cache.getEntry(activeQueryKey))?.value;
      templatesQueryCache.cache.set(activeQueryKey, [updatedRecord, ...(currentEntries ?? []).filter(s => s.id !== updatedRecord.id)]);
      return updatedRecord;
    },
  }), [kaiProApiClient, queryCache]);
};

export type ScanTemplateMutators = ReturnType<typeof useScanTemplateMutators>;

export const useScanTemplates = (options?: { archived?: boolean }) => {
  const queryCache = useQueryCache();
  const kaiProApiClient = useKaiProApiClient();

  const queryKey = getScanTemplatesQueryKey(options?.archived);

  const query = useCachedQuery<ApiScanTemplate[]>(
    queryKey,
    (key, queryCache) => fetchAndPopulateSingletonQueries(kaiProApiClient.getScanTemplates({ archived: options?.archived ? true : undefined }), queryCache),
    {
      enabled: !!kaiProApiClient.token,
    },
  );

  const scanTemplates = query.data || [];

  return {
    ...query,
    scanTemplates,
    scanTemplatesMap: object(scanTemplates, { withKey: ({ id }) => id }),
    scanTemplateMutators: useScanTemplateMutators(kaiProApiClient, queryCache),
  };
};

export const useScanTemplate = (scanTemplateId?: string) => {
  const queryCache = useQueryCache();
  const kaiProApiClient = useKaiProApiClient();

  const scanTemplateQueryKey = getScanTemplateQueryKey(scanTemplateId);
  const scanTemplateDataQueryKey = getScanTemplateRootQueryKey(scanTemplateId);

  const scanTemplateQuery = useCachedQuery<ApiScanTemplate>(
    scanTemplateQueryKey,
    () => kaiProApiClient.getScanTemplate(scanTemplateId!),
    {
      enabled: !!kaiProApiClient.token && !!scanTemplateId,
      staleAgeMs: 1000 * secondsPer.minute * 15,
    },
  );

  const scanTemplateDataQuery = useCachedQuery<ScanTemplateRoot>(
    scanTemplateDataQueryKey,
    () => kaiProApiClient.getScanTemplateRoot(scanTemplateId!),
    {
      enabled: !!kaiProApiClient.token && !!scanTemplateId && !!scanTemplateQuery.data,
      staleAgeMs: 1000 * secondsPer.minute * 15,
    },
  );

  const scanTemplate = scanTemplateQuery.data;
  const scanTemplateData = scanTemplateDataQuery.data;

  const scanTemplateMutators = useScanTemplateMutators(kaiProApiClient, queryCache);
  const scanTemplateDataMutators = useScanTemplateDataMutators(
    async (scanTemplateDataUpdater: (scanTemplateData: ScanTemplateRoot) => ScanTemplateRoot): Promise<void> => {
      return new Promise<void>(async (resolve, reject) => {
        if (!scanTemplateId) {
          reject(new Error('No scan template id available'));
          return;
        }
        // Get current scan template data from React Query cache
        const currentScanTemplateData = (await queryCache.cache.getEntry(scanTemplateDataQueryKey))?.value as ScanTemplateRoot;

        if (!currentScanTemplateData) {
          reject(new Error('No current scan template data available'));
          return;
        }

        // Apply the update mutation function to get new scan template data
        const updatedScanTemplateData = getNormalizedScanTemplateRoot(scanTemplateDataUpdater(currentScanTemplateData));

        // Update React Query cache with the updated scan template data
        await queryCache.cache.set(scanTemplateDataQueryKey, updatedScanTemplateData);

        // Save the delta to the server
        const delta = getScanDataInMemoryDeltaFromScanTemplateRoots(scanTemplateId, currentScanTemplateData, updatedScanTemplateData);
        console.log('Saving scanTemplateId delta', { delta, currentScanTemplateData, updatedScanTemplateData });
        console.log(`Saving scanTemplateId: ${scanTemplateId} delta. Records: ${delta.length}`);
        kaiProApiClient.saveScanTemplateDataDelta(scanTemplateId, delta)
          .then(() => {
            console.log(`Scan template data saved: ${scanTemplateId}`);
            resolve();
          })
          .catch(error => {
            console.error(`Error saving scan template data: ${scanTemplateId}`, error);
            reject(error);
          });
      });
    },
  );

  // Use scan template data from query (cache will be updated by mutations)
  const finalScanTemplateData = scanTemplateData;

  const combinedStatusInfo = useCombinedCachedQueryStatus(scanTemplateQuery, scanTemplateDataQuery);

  return { scanTemplate, scanTemplateData: finalScanTemplateData, scanTemplateMutators, scanTemplateDataMutators, ...combinedStatusInfo };
};
